import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  styled,
  Theme,
  ButtonProps,
  TextField,
  useTheme,
  ClickAwayListener,
} from '@quillbot/design-system';
import commonTrack from '@Common/handlers/track';
import { useDebounce } from '@Common/hooks';

type StyledButtonProps = ButtonProps & { active: boolean };

const StyledButton = styled(Button)<StyledButtonProps>(({ active, theme }: { active?: boolean; theme: Theme }) => ({
  fontWeight: active ? 700 : 600,
  fontSize: '14px',
  lineHeight: '18px',
  width: '104px',
  background: active ? theme.palette.primary.main : theme.palette.grey[50],
  color: active ? theme.palette.grey[50] : theme.palette.primary.main,
  border: '1px solid',
  borderColor: theme.palette.primary.main,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  background: theme.palette.grey[50],
  borderRadius: '4px',
  '& input': {
    height: '32px',
    padding: '12px',
    fontSize: '14px',
    color: theme.palette.text.primary,
    fontWeight: 700,
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
}));

function ExpertReviewPriceBlock({ variant = 'default' }: { variant: string }) {
  const theme = useTheme();
  const TAB_ID_MAP = {
    FIRST: '24_HOURS',
    SECOND: '72_HOURS',
  };
  const SETUP_FEE = 0;
  const HOUR_24_PRICE = 0.034;
  const HOUR_72_PRICE = 0.0255;

  const [tab, setTab] = useState(TAB_ID_MAP.FIRST);
  const [words, setWords] = useState<any>(0);
  const [pricePerWord, setPricePerWord] = useState(HOUR_24_PRICE);
  const [totalPrice, setTotalPrice] = useState(0);
  const debounceTotalPrice = useDebounce<any>(totalPrice);

  function handleInputChange(e: any) {
    const value = e.target.value;
    const re = /^[0-9\b]+$/;
    if (value === '' || re.test(value)) {
      setWords(value);
      calculatePrice(value, pricePerWord);
    }
  }

  useEffect(() => {
    if (debounceTotalPrice > 0) commonTrack.calculatedPriceOnExpertReview(debounceTotalPrice.toFixed(2), tab, words);
  }, [debounceTotalPrice]);

  function calculatePrice(wordsCount: any, price: number) {
    const final = price * wordsCount;
    if (wordsCount > 0) setTotalPrice(final + SETUP_FEE);
    else setTotalPrice(final);
  }

  function tabClickHandler(tab_id: string) {
    setTab(tab_id);
    if (tab_id === TAB_ID_MAP.FIRST) {
      setPricePerWord(HOUR_24_PRICE);
      calculatePrice(words, HOUR_24_PRICE);
    } else {
      setPricePerWord(HOUR_72_PRICE);
      calculatePrice(words, HOUR_72_PRICE);
    }
  }

  function handleClickAway() {
    if (words > 0) {
      setWords(words.replace(/^0+/, ''));
    }
  }

  return (
    <>
      <Box display="flex" justifyContent={variant === 'inline' ? 'space-between' : 'center'}>
        <Box flex={1}>
          <Box display="flex" alignItems="top">
            <Box pt="10px">
              <Box
                height={16}
                width={16}
                borderRadius="100%"
                bgcolor={theme.specialColors.ICON_DEFAULT_COLOR}
                justifyContent="center"
                display="flex"
                alignItems="center">
                <Typography fontWeight={600} color="grey.50" fontSize="10px">
                  1
                </Typography>
              </Box>
            </Box>
            <Box pr="6px" />
            <Box>
              <Box>
                <Typography color="text.primary" fontSize="16px" lineHeight="35px">
                  Turnaround time
                </Typography>
              </Box>
              <Box pt="8px" />
              <Box display="flex">
                <StyledButton
                  sx={{
                    '&:hover': {
                      color: tab === TAB_ID_MAP.FIRST ? theme.palette.grey[50] : theme.palette.primary.main,
                      bgcolor:
                        tab === TAB_ID_MAP.FIRST ? theme.palette.primary.dark : `${theme.palette.primary.main}0a`,
                    },
                  }}
                  onClick={() => tabClickHandler(TAB_ID_MAP.FIRST)}
                  variant="contained"
                  active={tab === TAB_ID_MAP.FIRST}>
                  24 HOURS
                </StyledButton>
                <Box pr="16px" />
                <StyledButton
                  sx={{
                    '&:hover': {
                      color: tab === TAB_ID_MAP.SECOND ? theme.palette.grey[50] : theme.palette.primary.main,
                      bgcolor:
                        tab === TAB_ID_MAP.SECOND ? theme.palette.primary.dark : `${theme.palette.primary.main}0a`,
                    },
                  }}
                  onClick={() => tabClickHandler(TAB_ID_MAP.SECOND)}
                  active={tab === TAB_ID_MAP.SECOND}
                  variant="contained">
                  72 HOURS
                </StyledButton>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box pr="60px" />
        <Box flex={1} display="inline-flex" justifyContent="center">
          <Box ml={variant === 'inline' ? '-8px' : 0}>
            <Box display="flex" alignItems="top">
              <Box pt="10px">
                <Box
                  height={16}
                  width={16}
                  borderRadius="100%"
                  bgcolor={theme.specialColors.ICON_DEFAULT_COLOR}
                  justifyContent="center"
                  display="flex"
                  alignItems="center">
                  <Typography fontWeight={600} color="grey.50" fontSize="10px">
                    2
                  </Typography>
                </Box>
              </Box>
              <Box pr="6px" />
              <Box>
                <Typography whiteSpace="nowrap" color="text.primary" fontSize="16px" lineHeight="35px">
                  Number of words
                </Typography>
                <Box pt="8px" />
                <ClickAwayListener onClickAway={() => (words ? handleClickAway() : () => {})}>
                  <Box height={36} width={76}>
                    <StyledTextField
                      onFocus={(e) => {
                        setTimeout(() => {
                          e.target.select();
                        }, 0);
                      }}
                      value={words}
                      onChange={(e) => handleInputChange(e)}
                      name="words"
                    />
                  </Box>
                </ClickAwayListener>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box pr="60px" />
        <Box flex={1}>
          <Box display="flex" alignItems="center">
            <Typography color="text.primary" fontSize="16px" lineHeight="35px">
              Final price
            </Typography>
          </Box>
          <Box pt="8px" />
          <Typography fontSize="12px" pt="4px" lineHeight="16px" color="text.primary">
            <Typography fontSize="20px" lineHeight="24px" fontWeight={600} color="text.primary" component="span">
              ${totalPrice.toFixed(2)}
            </Typography>
            &nbsp; ${pricePerWord}/ word
          </Typography>
          {/* {variant === 'inline' && (
            <>
              <Box pt="8px" />
              <Divider sx={{ borderColor: 'text.secondary' }} />
              <Box pt="8px" />
              <Typography fontStyle="italic" fontSize="14px" lineHeight="18px" color="text.primary">
                * Final price includes a <b>$25</b> setup fee
              </Typography>
            </>
          )} */}
        </Box>
      </Box>
      {variant !== 'inline' && (
        <>
          {/* <Box pt="16px" />
          <Divider sx={{ borderColor: 'text.secondary' }} />
          <Box pt="8px" /> */}
          {/* <Typography fontStyle="italic" fontSize="16px" lineHeight="20px" color="text.primary" textAlign="center">
            * Final price includes a <b>$25</b> setup fee
          </Typography> */}
        </>
      )}
    </>
  );
}

export default ExpertReviewPriceBlock;
